/* eslint-disable max-statements */
/* eslint-disable no-unsafe-optional-chaining */
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { AGENTROLE, MANAGERROLE, SUPERVISORROLE } from '../../constants';
import { selectCurrentRole } from '../../pages/login/authSlice';
import './AttainmentChart.css';
import { MetricTargetsResult } from './types';
import { ConstScorecardRole } from '../../utils/typeConstants';
import { getArcPercent } from '../../utils/metricAttainment';
import { StackRankBandColors, StackRankEntityColors } from '../../utils/stackRankColors';

interface TieredAttainmentChartProps {
    data: MetricTargetsResult;
    selectedUserInGrid?: any;
    empNum?: number;
    metricType?: string;
    scorecardRoleId?: number;
    // Metric color family index - same convention as everywhere else (a metric's position
    // among the displayed metrics, mod palette length). Computed by the caller (StackRank.tsx).
    colorIndex: number;
}

type TieredStatus = 'belowMeets' | 'meets' | 'exceeds';

/**
 * Tiered (P4P multi-tier, E20-6317) version of the Stack Rank attainment chart. Deliberately a
 * separate component from AttainmentChart.tsx rather than a variant branch inside it - the two
 * share no internals, so a change to one can never affect the legacy chart, same reasoning as
 * LegacyMetricGauge/TieredMetricGauge.
 *
 * Geometry, per-band structure, and the value-proportional arc fill (stroke-dasharray) are
 * unchanged from the legacy chart - only the color source and the meaning of the dot marker
 * differ:
 *   - Band color: each entity's own Below Meets/Meets/Exceeds status selects one of 3 shades
 *     (see stackRankColors.ts) within this metric's color family, replacing the legacy fixed
 *     per-entity hue (purple/blue/teal/green).
 *   - Dot marker: legacy marks each entity's own TARGET position. In the tiered model there's no
 *     longer a distinct per-entity target - Meets/Exceeds are metric-level thresholds shared by
 *     all 4 entities - so a target-dot would sit at the identical position for every band. The
 *     reference design shows each entity's dot at a clearly different position, which only makes
 *     sense if it marks that entity's own VALUE - i.e., the tip of its own arc fill. This is
 *     the one behavioral difference from "the information doesn't change" and is called out here
 *     explicitly rather than silently.
 */
const TieredAttainmentChart: React.FC<TieredAttainmentChartProps> = ({
    data,
    selectedUserInGrid,
    empNum,
    metricType,
    scorecardRoleId,
    colorIndex,
}) => {
    const {
        agentMetricValue = 0,
        supervisorMetricValue = 0,
        scorecardMetricValue = 0,
        scorecardTopPercentileMetricValue = 0,
        minValue = 0,
        maxValue = 0,
        meetsTarget,
        exceedsTarget,
    } = data;

    const formatValue = (value: number): string => {
        if (metricType?.toLowerCase() === 'percentage') {
            const val = value * 100;
            return `${val.toFixed(2)}%`;
        }
        return value.toString();
    };

    const chartContainerRef = useRef<HTMLDivElement>(null);
    const [hoveredMetric, setHoveredMetric] = useState<any>(null);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

    const role = useSelector(selectCurrentRole);

    let agentName = 'Me';
    let teamName = 'My Team';
    let scorecardName = 'My Scorecard';

    if (!selectedUserInGrid || Object.keys(selectedUserInGrid).length === 0) {
        agentName = 'Me';
        teamName = 'Team';
        scorecardName = 'Scorecard';
    } else if (selectedUserInGrid?.employee_number === empNum) {
        agentName = 'Me';
        teamName = 'My Team';
        scorecardName = 'My Scorecard';
    } else {
        const employeeName = selectedUserInGrid?.employee_name || '';
        if (employeeName.startsWith('Anonymous')) {
            agentName = 'Anonymous';
            teamName = 'Anonymous - Team';
            scorecardName = 'Anonymous - Scorecard';
        } else {
            const firstName = selectedUserInGrid?.first_name || '';
            agentName = firstName;
            teamName = `${firstName} - Team`;
            scorecardName = `${firstName} - Scorecard`;
        }
    }

    const colorSet = StackRankBandColors[colorIndex] ?? StackRankBandColors[0];

    // Effective max for ALL internal position/normalization math (arcs, dots, labels) - NOT for
    // the displayed "Max:" text, which always shows the metric's actual configured maxValue.
    // Exceeds can legitimately be configured beyond the nominal maxValue (a stretch target past
    // the "typical" range) - when that happens, extending the scale here means Exceeds still
    // lands in its correct relative position instead of being clamped on top of Max.
    //
    // Direction-aware: min/max are pre-oriented "worst->best" by the backend per
    // metric_direction_id, so "beyond max" means numerically LARGER for an ascending metric
    // (maxValue > minValue) but numerically SMALLER for a descending one (maxValue < minValue,
    // e.g. Min=100%/Max=0%). Using a plain Math.max() here regardless of direction was the actual
    // bug: for a descending metric it could incorrectly "extend" the scale using a value that was
    // really just worse-than-Max-but-still-within-range, collapsing the normalize() denominator
    // toward zero and causing exactly the labels-collapsing-together symptom seen after the fact.
    const isAscendingScale = maxValue >= minValue;
    const effectiveMaxValue = (() => {
        if (exceedsTarget == null) {
            return maxValue;
        }
        return isAscendingScale ? Math.max(maxValue, exceedsTarget) : Math.min(maxValue, exceedsTarget);
    })();

    // Status per entity, driven purely by where its value falls between min/meetsTarget/
    // exceedsTarget/max on the same normalized 0-100 scale used elsewhere (getArcPercent) -
    // min/max are already direction-oriented server-side, so no direction branching needed here.
    const getStatus = (value: number): TieredStatus => {
        const valuePercent = getArcPercent(value, minValue, effectiveMaxValue);
        const meetsPercent = getArcPercent(meetsTarget, minValue, effectiveMaxValue);
        const exceedsPercent = getArcPercent(exceedsTarget, minValue, effectiveMaxValue);
        if (valuePercent >= exceedsPercent && exceedsPercent > 0) {
            return 'exceeds';
        }
        if (valuePercent >= meetsPercent && meetsPercent > 0) {
            return 'meets';
        }
        return 'belowMeets';
    };

    const statusLabel = (status: TieredStatus): string => {
        if (status === 'exceeds') return 'Exceeds';
        if (status === 'meets') return 'Meets';
        return 'Below Meets';
    };

    const buildMetric = (
        name: string,
        value: number,
        radius: number,
        entityKey: keyof typeof StackRankEntityColors
    ) => {
        const status = getStatus(value);
        // Progressively thicker for the outer/larger-radius bands, thinner toward the inner/
        // smaller-radius ones (radius 140 -> 16px down to radius 80 -> 10px).
        const strokeWidth = 10 + ((radius - 80) * 6) / 60;
        return {
            name,
            value,
            status,
            // Band (arc fill/track) color - metric family + this entity's own status. Distinct
            // from dotColor below: the dot/legend-swatch identity color is fixed per entity and
            // must NOT change when a different metric is selected, per explicit instruction.
            bandColor: colorSet[status],
            dotColor: StackRankEntityColors[entityKey],
            radius,
            strokeWidth,
        };
    };

    const metrics = [
        ...((scorecardRoleId === ConstScorecardRole.ASSOCIATE &&
            selectedUserInGrid &&
            Object.keys(selectedUserInGrid).length > 0 &&
            (role === SUPERVISORROLE || role === MANAGERROLE)) ||
            role === AGENTROLE
            ? [buildMetric(agentName, agentMetricValue, 140, 'me')]
            : []),
        ...(((selectedUserInGrid &&
            Object.keys(selectedUserInGrid).length > 0 &&
            role === MANAGERROLE) ||
            role === SUPERVISORROLE ||
            role === AGENTROLE)
            ? [buildMetric(teamName, supervisorMetricValue, 120, 'team')]
            : []),
        buildMetric(scorecardName, scorecardMetricValue, 100, 'scorecard'),
        buildMetric('Top Performers', scorecardTopPercentileMetricValue, 80, 'topPerformers'),
    ];

    const normalizeValue = (value: number): number => {
        return value === 0
            ? 0
            : Math.min(Math.max((value - minValue) / (effectiveMaxValue - minValue), 0), 1);
    };

    const calculateStrokeDashArray = (value: number, radius: number): [number, number] => {
        const circumference = Math.PI * radius;
        const normalizedValue = normalizeValue(value);
        return [normalizedValue * circumference, circumference];
    };

    const handleMouseEnter = (metric: any) => setHoveredMetric(metric);
    const handleMouseLeave = () => setHoveredMetric(null);
    const handleMouseMove = (e: React.MouseEvent) => {
        const rect = chartContainerRef.current?.getBoundingClientRect();
        if (rect) {
            setMousePosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        }
    };

    // Bottom Min/Meets/Exceeds/Max labels: FIXED, evenly-spaced positions in this exact literal
    // order - not sorted by value, not proportional to the values. Always Min, Meets, Exceeds,
    // Max left-to-right, whichever of Meets/Exceeds are present.
    type LabelKey = 'min' | 'meets' | 'exceeds' | 'max';
    const allLabels: { key: LabelKey; text: string }[] = [
        { key: 'min', text: `Min: ${formatValue(minValue)}` },
        ...(meetsTarget != null ? [{ key: 'meets' as const, text: `Meets: ${formatValue(meetsTarget)}` }] : []),
        ...(exceedsTarget != null
            ? [{ key: 'exceeds' as const, text: `Exceeds: ${formatValue(exceedsTarget)}` }]
            : []),
        { key: 'max', text: `Max: ${formatValue(maxValue)}` },
    ];

    const LABEL_LEFT_BOUND = 15;
    const LABEL_RIGHT_BOUND = 235;
    const labelSpacing =
        allLabels.length > 1 ? (LABEL_RIGHT_BOUND - LABEL_LEFT_BOUND) / (allLabels.length - 1) : 0;
    const positionedLabels = allLabels.map((label, index) => ({
        ...label,
        x: LABEL_LEFT_BOUND + index * labelSpacing,
    }));

    return (
        <div className="attainment-chart">
            <div className="attainment-header">
                <h3 className="attainment-title">Metric Attainment</h3>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
                <div className="chart-container" ref={chartContainerRef} style={{ position: 'relative' }}>
                    <svg width="100%" height="100%" viewBox="0 0 250 200">
                        {metrics.map((metric, index) => {
                            const radius = metric.radius;
                            const [dashLength, dashGap] = calculateStrokeDashArray(metric.value, radius);

                            // Dot marks this entity's own VALUE position (see class comment above
                            // for why this differs from the legacy target-position dot).
                            const normalizedValue = normalizeValue(metric.value);
                            const valueAngle = Math.PI * (1 - normalizedValue);

                            return (
                                <g key={index}>
                                    <path
                                        d={`M ${125 - radius} 170 A ${radius} ${radius} 0 0 1 ${
                                            125 + radius
                                        } 170`}
                                        fill="none"
                                        stroke={metric.bandColor}
                                        strokeWidth={metric.strokeWidth}
                                        strokeOpacity="0.2"
                                        onMouseEnter={() => handleMouseEnter(metric)}
                                        onMouseLeave={handleMouseLeave}
                                        onMouseMove={handleMouseMove}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    <path
                                        d={`M ${125 - radius} 170 A ${radius} ${radius} 0 0 1 ${
                                            125 + radius
                                        } 170`}
                                        fill="none"
                                        stroke={metric.bandColor}
                                        strokeWidth={metric.strokeWidth}
                                        strokeDasharray={`${dashLength} ${dashGap}`}
                                        strokeDashoffset="0"
                                        onMouseEnter={() => handleMouseEnter(metric)}
                                        onMouseLeave={handleMouseLeave}
                                        onMouseMove={handleMouseMove}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    <circle
                                        cx={125 + radius * Math.cos(valueAngle)}
                                        cy={170 - radius * Math.sin(valueAngle)}
                                        r={metric.strokeWidth / 2}
                                        fill={metric.dotColor}
                                        stroke="white"
                                        strokeWidth={Math.max(1, metric.strokeWidth / 8)}
                                    />
                                </g>
                            );
                        })}

                        {positionedLabels.map((label) => (
                            <text
                                key={label.key}
                                x={label.x}
                                y="187"
                                className="min-max-label"
                                textAnchor="middle"
                                style={{ fill: colorSet.meets }}
                            >
                                {label.text}
                            </text>
                        ))}
                    </svg>

                    {hoveredMetric && (
                        <div
                            className="hover-tooltip"
                            style={{
                                position: 'absolute',
                                left: `${mousePosition.x}px`,
                                top: `${mousePosition.y - 80}px`,
                                backgroundColor: 'white',
                                padding: '8px 12px',
                                borderRadius: '4px',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                                zIndex: 100,
                                pointerEvents: 'none',
                                whiteSpace: 'nowrap',
                                transform: 'translate(-50%, 0)',
                            }}
                        >
                            <div
                                className="tooltip-title"
                                style={{ color: hoveredMetric.dotColor, fontWeight: 'bold', marginBottom: '4px' }}
                            >
                                {hoveredMetric.name}
                            </div>
                            <div className="tooltip-value">Value: {formatValue(hoveredMetric.value)}</div>
                            <div className="tooltip-value">{statusLabel(hoveredMetric.status)}</div>
                        </div>
                    )}
                </div>

                <div className="attainment-legend">
                    {metrics.map((metric, index) => (
                        <div key={index} className="legend-item">
                            <div className="legend-color" style={{ backgroundColor: metric.dotColor }} />
                            <div className="legend-text">
                                <div className="legend-name">{metric.name}</div>
                                <div className="legend-value">
                                    {formatValue(metric.value)}
                                    <span style={{ color: metric.bandColor, fontWeight: 700, marginLeft: 6 }}>
                                        - {statusLabel(metric.status)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default TieredAttainmentChart;
